import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Button — the one action control both apps use.
 *
 * ## Loading and disabled are different states, and are spelled differently
 *
 * Requirement 7.7 asks for both to be announced. They are not the same announcement:
 *
 * - **Disabled** is the native attribute. A `disabled` button is removed from the tab order and
 *   reports `aria-disabled` implicitly, which is what a reader expects from a control that is not
 *   available at all.
 * - **Loading** keeps the button focusable and adds `aria-busy="true"`, `aria-disabled="true"`, and a
 *   visually hidden `role="status"` carrying the loading label. Focus is kept on purpose: an async
 *   action is usually started by the control that is now busy, and a `disabled` attribute applied on
 *   submit throws focus to the document body mid-interaction, which is how a keyboard user loses their
 *   place in a form. `aria-disabled` says "not actionable right now" without moving anyone, and the
 *   click and key handlers refuse the activation so the promise cannot be started twice.
 *
 * The spinner is `aria-hidden`: it is the same information the live region already carries, and a
 * reader announcing it twice is worse than not announcing it. Its `animate-spin` collapses under
 * `prefers-reduced-motion` through the generated stylesheet rather than through a branch here.
 */
import { cva } from 'class-variance-authority';
import { cn } from '../lib/cn.js';
import { focusRing } from '../lib/treatments.js';
/**
 * Every fill is a surface token carrying its own foreground token. That pairing is what the contrast
 * lint checks, and it is why there is no `variant` here whose ink is red: red on navy is 2.94:1, so on
 * dark surfaces red is a fill and never an ink.
 */
export const buttonVariants = cva(cn('inline-flex shrink-0 items-center justify-center gap-2 rounded-lg font-body font-medium', 'transition-colors duration-fast ease-standard', focusRing, 
// Both spellings, because a disabled button and a busy one look the same and are told apart by
// assistive technology rather than by eye.
'disabled:pointer-events-none disabled:opacity-60', 'aria-disabled:pointer-events-none aria-disabled:opacity-60'), {
    variants: {
        variant: {
            primary: 'bg-primary text-primary-foreground hover:bg-apr-redDeep',
            secondary: 'bg-secondary text-secondary-foreground hover:bg-muted',
            destructive: 'bg-destructive text-destructive-foreground hover:bg-apr-red',
            outline: 'border border-input bg-transparent text-foreground hover:bg-secondary hover:text-secondary-foreground',
            ghost: 'text-foreground hover:bg-secondary hover:text-secondary-foreground',
        },
        size: {
            sm: 'h-9 px-3 text-sm',
            md: 'h-10 px-4 text-sm',
            lg: 'h-11 px-6 text-base',
        },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
});
const Spinner = () => (_jsxs("svg", { className: "h-4 w-4 animate-spin", viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true", focusable: "false", children: [_jsx("circle", { cx: "12", cy: "12", r: "9", stroke: "currentColor", strokeWidth: "2.5", opacity: "0.25" }), _jsx("path", { d: "M21 12a9 9 0 0 0-9-9", stroke: "currentColor", strokeWidth: "2.5", strokeLinecap: "round" })] }));
export const Button = ({ className, variant, size, loading = false, loadingLabel = 'Procesando…', disabled = false, type = 'button', onClick, children, ...rest }) => {
    const handleClick = (event) => {
        if (loading) {
            // `aria-disabled` is advisory; the browser will still deliver the click. This is the half that
            // makes it true, and it covers Enter and Space as well, since both arrive as a click.
            event.preventDefault();
            return;
        }
        onClick?.(event);
    };
    return (_jsxs("button", { type: type, className: cn(buttonVariants({ variant, size }), className), disabled: disabled, "aria-disabled": loading ? true : undefined, "aria-busy": loading ? true : undefined, "data-loading": loading ? 'true' : undefined, onClick: handleClick, ...rest, children: [loading ? _jsx(Spinner, {}) : null, children, loading ? (_jsx("span", { role: "status", className: "sr-only", children: loadingLabel })) : null] }));
};
//# sourceMappingURL=button.js.map